const normalizeString = (value) => String(value || '').trim().toLowerCase();

const resolveDateKey = (value) => {
  const raw = String(value || '').trim();
  if (raw === '') {
    return '';
  }

  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? String(match[1]).trim() : '';
};

const formatDateForFilter = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const buildCurrentMonthRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  return {
    date_from: formatDateForFilter(new Date(year, month, 1)),
    date_to: formatDateForFilter(new Date(year, month + 1, 0)),
  };
};

export const createDefaultInvoicesToolbarFilters = () => ({
  ...buildCurrentMonthRange(),
  customer_ids: [],
  has_business: [],
  business_ids: [],
  has_received_proof: [],
  invoice_types: [],
});

export const normalizeInvoicesToolbarFilters = (filters) => {
  const safeFilters = filters && typeof filters === 'object' ? filters : {};

  return {
    date_from: String(safeFilters.date_from || '').trim(),
    date_to: String(safeFilters.date_to || '').trim(),
    customer_ids: Array.isArray(safeFilters.customer_ids) ? safeFilters.customer_ids : [],
    has_business: Array.isArray(safeFilters.has_business) ? safeFilters.has_business : [],
    business_ids: Array.isArray(safeFilters.business_ids) ? safeFilters.business_ids : [],
    has_received_proof: Array.isArray(safeFilters.has_received_proof) ? safeFilters.has_received_proof : [],
    invoice_types: Array.isArray(safeFilters.invoice_types) ? safeFilters.invoice_types : [],
  };
};

const matchesDateRange = (row, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) {
    return true;
  }

  const rowDateKey = resolveDateKey(row?.date);
  if (rowDateKey !== '') {
    if (dateFrom && rowDateKey < dateFrom) {
      return false;
    }

    if (dateTo && rowDateKey > dateTo) {
      return false;
    }

    return true;
  }

  const rowDate = new Date(String(row?.date || ''));
  if (Number.isNaN(rowDate.getTime())) {
    return false;
  }

  const startDate = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
  const endDate = dateTo ? new Date(`${dateTo}T23:59:59`) : null;

  if (startDate && rowDate < startDate) {
    return false;
  }

  if (endDate && rowDate > endDate) {
    return false;
  }

  return true;
};

const includesValue = (selectedValues, value) => {
  if (!Array.isArray(selectedValues) || selectedValues.length === 0) {
    return true;
  }

  return selectedValues.map((item) => String(item)).includes(String(value));
};

export const filterInvoicesRowsByToolbar = (rows, { search, filters }) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const normalizedSearch = normalizeString(search);
  const normalizedFilters = normalizeInvoicesToolbarFilters(filters);

  return safeRows.filter((row) => {
    if (normalizedSearch !== '') {
      const serial = String(row?.serial || '').trim();
      const folio = String(row?.folio || '').trim();
      const serialFolio = `${serial}${folio}`.trim();
      const serialFolioSpaced = `${serial} ${folio}`.trim();

      const haystack = [
        folio,
        serial,
        serialFolio,
        serialFolioSpaced,
        row?.uuid,
        row?.customer_name,
        row?.company_name,
        row?.sale_name,
        row?.business_name,
      ]
        .map((item) => normalizeString(item))
        .join(' ');

      if (!haystack.includes(normalizedSearch)) {
        return false;
      }
    }

    if (!matchesDateRange(row, normalizedFilters.date_from, normalizedFilters.date_to)) {
      return false;
    }

    if (!includesValue(normalizedFilters.customer_ids, row?.customer_id)) {
      return false;
    }

    if (!includesValue(normalizedFilters.business_ids, row?.sale_id)) {
      return false;
    }

    if (normalizedFilters.has_business.length > 0) {
      const hasBusinessValue = Number(row?.sale_id || 0) > 0 ? 'yes' : 'no';
      if (!normalizedFilters.has_business.includes(hasBusinessValue)) {
        return false;
      }
    }

    if (normalizedFilters.has_received_proof.length > 0) {
      const hasProofValue = row?.has_received_proof ? 'yes' : 'no';
      if (!normalizedFilters.has_received_proof.includes(hasProofValue)) {
        return false;
      }
    }

    if (normalizedFilters.invoice_types.length > 0) {
      const typeValue = normalizeString(row?.invoice_type);
      if (!normalizedFilters.invoice_types.includes(typeValue)) {
        return false;
      }
    }

    return true;
  });
};
